import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const DiscountReportsTab = () => {
  const [discountReportData, setDiscountReportData] = useState([]);
  const [discountReportFilters, setDiscountReportFilters] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    discountId: ''
  });
  const [topDiscounts, setTopDiscounts] = useState([]);
  const [discountStats, setDiscountStats] = useState({});
  const [reportType, setReportType] = useState('dzienne');
  const [discountDetails, setDiscountDetails] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- API functions ---

  const loadDiscounts = useCallback(async () => {
    try {
      const response = await api.get('/rabaty?aktywny=all');
      const data = response.data;
      const discountsList = data.data?.rabaty || data.message?.rabaty || [];
      setDiscounts(discountsList);
    } catch (err) {
      console.error('Blad podczas pobierania rabatow:', err);
    }
  }, []);

  const loadDiscountReports = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (reportType === 'dzienne') {
        params.append('data_od', discountReportFilters.dateFrom);
        params.append('data_do', discountReportFilters.dateTo);
      } else {
        const miesiac = discountReportFilters.dateFrom.substring(0, 7);
        params.append('miesiac', miesiac);
      }

      if (discountReportFilters.discountId) {
        params.append('rabat_id', discountReportFilters.discountId);
      }

      const endpoint = reportType === 'dzienne' ? 'dzienne' : 'miesieczne';
      const response = await api.get(`/rabaty/raporty/${endpoint}?${params}`);

      const data = response.data;
      const reports = data.message?.raporty || [];

      const mappedReports = reports.map(item => {
      const wartoscRabatu = item.typ_rabatu === 'procentowy'
        ? (item.rabat_wartosc || Math.round((item.suma_rabatow / item.suma_przed_rabatem) * 100))
        : item.suma_rabatow;

      return {
        data_transakcji: item.dzien,
        godzina: item.godzina || '',
        nazwa_rabatu: item.rabat_nazwa,
        typ_rabatu: item.typ_rabatu,
        wartosc_rabatu: wartoscRabatu,
        klient_nazwa: item.kasjer,
        wartosc_przed_rabatem: item.suma_przed_rabatem,
        kwota_rabatu: item.suma_rabatow,
        wartosc_po_rabacie: item.suma_po_rabacie,
        transakcja_id: item.numer_paragonu || item.transakcja_id,
        kasjer: item.kasjer,
        ilosc_uzyc: item.ilosc_uzyc || 1,
        kod_weryfikacyjny: item.kod_weryfikacyjny || '',
        numer_karty_klienta: item.numer_karty_klienta || '',
        nazwa_influencera: item.nazwa_influencera || ''
      };
      });

      setDiscountReportData(mappedReports);
    } catch (err) {
      console.error('Blad podczas pobierania raportow rabatow:', err);
      setDiscountReportData([]);
    }
  }, [reportType, discountReportFilters.dateFrom, discountReportFilters.dateTo, discountReportFilters.discountId]);

  const loadDiscountStats = useCallback(async () => {
    try {
      const response = await api.get('/rabaty/stats');
      const data = response.data;
      const uzycie = data.data?.uzycie || {};
      const rabaty = data.data?.rabaty || {};
      setDiscountStats({
      total_savings: uzycie.suma_rabatow || 0,
      total_uses: uzycie.total_uzyc || 0,
      avg_discount: uzycie.sredni_rabat || 0,
      unique_customers: uzycie.uzycia_dzisiaj || 0,
      rabaty_dzisiaj: uzycie.rabaty_dzisiaj || 0,
      uzycia_dzisiaj: uzycie.uzycia_dzisiaj || 0,
      uzycia_ten_miesiac: uzycie.uzycia_ten_miesiac || 0,
      rabaty_ten_miesiac: uzycie.rabaty_ten_miesiac || 0,
      aktywne_rabaty: rabaty.aktywne_rabaty || 0,
      total_rabaty: rabaty.total_rabaty || 0
      });
    } catch (err) {
      console.error('Blad podczas pobierania statystyk rabatow:', err);
    }
  }, []);

  const generateDiscountReport = async () => {
    setLoading(true);
    try {
      await loadDiscountReports();
    } catch (error) {
      console.error('Blad podczas generowania raportu:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportDiscountReport = (data) => {
    const reportData = data || discountReportData;
    if (!reportData || reportData.length === 0) {
      toast.error('Brak danych do eksportu');
      return;
    }

    const csvContent = [
      ['Data', 'Rabat', 'Typ', 'Kasjer', 'Ilosc uzyc', 'Wartosc przed rabatem', 'Kwota rabatu', 'Wartosc po rabacie'],
      ...reportData.map(report => [
        report.data_transakcji || '',
        report.nazwa_rabatu || '',
        report.typ_rabatu || '',
        report.kasjer || '',
        report.ilosc_uzyc || 0,
        report.wartosc_przed_rabatem || 0,
        report.kwota_rabatu || 0,
        report.wartosc_po_rabacie || 0
      ])
    ].map(row => row.join(';')).join('\n');

    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `raport_rabaty_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportDiscountReportPDF = async () => {
    if (!discountReportData || discountReportData.length === 0) {
      toast.error('Brak danych do eksportu');
      return;
    }

    try {
      const params = new URLSearchParams({
        data_od: discountReportFilters.dateFrom,
        data_do: discountReportFilters.dateTo
      });
      if (discountReportFilters.discountId) {
        params.append('rabat_ids', discountReportFilters.discountId);
      }

      const response = await api.get(`/rabaty/raporty/dzienne/pdf?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `raport_rabaty_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Blad generowania PDF:', err);
      toast.error('Blad generowania PDF.');
    }
  };

  // --- Effects ---

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadDiscounts(), loadDiscountStats()]);
      setLoading(false);
    };
    init();
  }, [loadDiscounts, loadDiscountStats]);

  // --- Render ---

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Filtry raportow */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          Raporty rabatow
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Data od
            </label>
            <input
              type="date"
              value={discountReportFilters.dateFrom}
              onChange={(e) => setDiscountReportFilters({...discountReportFilters, dateFrom: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Data do
            </label>
            <input
              type="date"
              value={discountReportFilters.dateTo}
              onChange={(e) => setDiscountReportFilters({...discountReportFilters, dateTo: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
              Rabat
            </label>
            <select
              value={discountReportFilters.discountId}
              onChange={(e) => setDiscountReportFilters({...discountReportFilters, discountId: e.target.value})}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            >
              <option value="">Wszystkie rabaty</option>
              {discounts.map(discount => (
                <option key={discount.id} value={discount.id}>
                  {discount.nazwa}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'end' }}>
            <button
              onClick={generateDiscountReport}
              style={{
                width: '100%',
                padding: '6px 12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Generuj raport
            </button>
          </div>
        </div>
      </div>

      {/* Statystyki ogolne */}
      {discountStats && (
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          backgroundColor: 'var(--card-bg)',
          padding: '1rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
            Statystyki rabatow
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--info-light)',
              borderRadius: '0.375rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--info)', marginBottom: '0.25rem' }}>
                {discountStats.total_savings?.toFixed(2) || '0.00'} zl
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Laczne oszczednosci
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--success-light)',
              borderRadius: '0.375rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success)', marginBottom: '0.25rem' }}>
                {discountStats.total_uses || 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Calkowite uzycia
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--warning-light)',
              borderRadius: '0.375rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--warning)', marginBottom: '0.25rem' }}>
                {discountStats.avg_discount?.toFixed(2) || '0.00'} zl
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Sredni rabat
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--danger-light)',
              borderRadius: '0.375rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--danger)', marginBottom: '0.25rem' }}>
                {discountStats.uzycia_dzisiaj || 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Uzycia dzisiaj
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Raport szczegolowy */}
      {discountReportData && discountReportData.length > 0 && (
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          backgroundColor: 'var(--card-bg)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <span>Szczegolowy raport rabatow</span>
            <button
              onClick={() => exportDiscountReport(discountReportData)}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--success)', color: 'var(--success)' ,
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Eksportuj CSV
            </button>
            <button
              onClick={() => exportDiscountReportPDF(discountReportData)}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--danger)', color: 'var(--danger)' ,
                cursor: 'pointer',
                fontSize: '12px',
                marginLeft: '0.5rem'
              }}
            >
              Eksportuj PDF
            </button>
          </div>

          <div className="admin-table-wrap" style={{ overflow: 'auto', maxHeight: '500px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--bg)', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Data
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Godzina
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Rabat
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Klient
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Wartosc przed
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Rabat
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Wartosc koncowa
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Transakcja
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Kod weryfikacyjny
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    Nr karty klienta
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    Influencer
                  </th>
                </tr>
              </thead>
              <tbody>
                {discountReportData.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{
                      padding: '2rem',
                      textAlign: 'center',
                      fontSize: '0.875rem',
                      color: 'var(--text-muted)',
                      borderBottom: '1px solid var(--border)'
                    }}>
                      {loading ? 'Ladowanie danych...' : 'Brak danych do wyswietlenia. Sprawdz filtry lub dodaj rabaty w systemie POS.'}
                    </td>
                  </tr>
                ) : discountReportData.map((item, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {new Date(item.data_transakcji).toLocaleDateString('pl-PL')}
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {item.godzina ? item.godzina.substring(0, 5) : '-'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: '500' }}>{item.nazwa_rabatu}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {item.typ_rabatu === 'procentowy' ? `${item.wartosc_rabatu}%` : `${parseFloat(item.wartosc_rabatu).toFixed(2)} zl`}
                      </div>
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      {item.klient_nazwa || 'Brak danych'}
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {parseFloat(item.wartosc_przed_rabatem).toFixed(2)} zl
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      textAlign: 'right',
                      borderBottom: '1px solid var(--border)',
                      color: 'var(--danger)',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>
                      -{parseFloat(item.kwota_rabatu).toFixed(2)} zl
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {parseFloat(item.wartosc_po_rabacie).toFixed(2)} zl
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'var(--info-light)',
                        color: 'var(--info)',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem'
                      }}>
                        {item.transakcja_id}
                      </span>
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      {item.kod_weryfikacyjny ? (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--warning-light)',
                          color: 'var(--warning)',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace'
                        }}>
                          {item.kod_weryfikacyjny}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                      )}
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      {item.numer_karty_klienta ? (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--success-light)',
                          color: 'var(--success)',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace'
                        }}>
                          {item.numer_karty_klienta}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                      )}
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', fontSize: '0.875rem', borderBottom: '1px solid var(--border)' }}>
                      {item.nazwa_influencera ? (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--purple-light)',
                          color: 'var(--purple)',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem'
                        }}>
                          {item.nazwa_influencera}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Podsumowanie raportu */}
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--bg)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Pokazano {discountReportData.length} rekordow
            </span>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', flexWrap: 'wrap' }}>
              <span>
                <strong>Laczne oszczednosci:</strong> {' '}
                <span style={{ color: 'var(--danger)', fontWeight: '600' }}>
                  {discountReportData.reduce((sum, item) => sum + parseFloat(item.kwota_rabatu), 0).toFixed(2)} zl
                </span>
              </span>
              <span>
                <strong>Wartosc przed rabatami:</strong> {' '}
                <span style={{ fontWeight: '600' }}>
                  {discountReportData.reduce((sum, item) => sum + parseFloat(item.wartosc_przed_rabatem), 0).toFixed(2)} zl
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Top rabaty */}
      {topDiscounts && topDiscounts.length > 0 && (
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          backgroundColor: 'var(--card-bg)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            fontWeight: '600'
          }}>
            Najczesciej uzywane rabaty
          </div>

          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {topDiscounts.map((discount, index) => (
                <div key={discount.id} style={{
                  padding: '1rem',
                  border: '1px solid var(--border)',
                  borderRadius: '0.375rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: index === 0 ? 'var(--warning-light)' : 'var(--card-bg)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: index === 0 ? 'var(--warning)' : 'var(--text-muted)',
                      color: '#fff' ,
                      borderRadius: '50%',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      minWidth: '1.5rem',
                      textAlign: 'center'
                    }}>
                      {index + 1}
                    </span>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                        {discount.nazwa}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {discount.typ_rabatu === 'procentowy' ? `${discount.wartosc}%` : `${parseFloat(discount.wartosc).toFixed(2)} zl`}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                      {discount.uzycia_count} uzyc
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {parseFloat(discount.total_savings).toFixed(2)} zl oszczednosci
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Wykres trendow (placeholder) */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          Trendy uzycia rabatow
        </h3>
        <div style={{
          height: '200px',
          backgroundColor: 'var(--bg)',
          borderRadius: '0.375rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)'
        }}>
          Wykres trendow uzycia rabatow w czasie
          <br />
          <small>(wymagana integracja z biblioteka wykresow)</small>
        </div>
      </div>
    </div>
  );
};

export default DiscountReportsTab;
